import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, FileUp, Loader2 } from "lucide-react";
import { API, api, errMsg } from "@/lib/api";

/** Arbitrator-only upload of the signed PDF of a daily order, award or adjournment order. */
export const SignedPdf = ({ kind, targetId, documentId, viewId, lockedId, canUpload, onDone, testId, label = "signed order" }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Only PDF files may be attached");
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("target_id", targetId);
    fd.append("file", file);
    try {
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signed PDF uploaded — parties intimated");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      {documentId && (
        <a href={`${API}/documents/${documentId}/download`} target="_blank" rel="noreferrer"
          data-testid={`${testId}-download`}
          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
          <Download className="h-3.5 w-3.5" /> PDF
        </a>
      )}
      {!documentId && !viewId && lockedId && (
        <button type="button" disabled data-testid={`${testId}-download-locked`}
          title="Download opens once your side's subscription for this case is active"
          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground/60 cursor-not-allowed">
          <Download className="h-3.5 w-3.5" /> PDF · subscription required
        </button>
      )}
      {!documentId && viewId && (
        <a href={`${API}/documents/${viewId}/view`} target="_blank" rel="noreferrer"
          data-testid={`${testId}-view`}
          className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
          <Eye className="h-3.5 w-3.5" /> View
        </a>
      )}
      {canUpload && (
        <>
          <input ref={input} type="file" accept="application/pdf" className="hidden"
            data-testid={`${testId}-input`} onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" disabled={busy} data-testid={`${testId}-upload`}
            onClick={() => input.current?.click()}
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 hover:text-blue-900 dark:hover:text-blue-400 transition-colors duration-200">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
            {busy ? "Uploading…" : documentId ? "Replace PDF" : `Attach ${label} PDF`}
          </button>
        </>
      )}
    </div>
  );
};

export default SignedPdf;
